import { createReactBlockSpec } from '@blocknote/react';
import { useBlockNoteEditor } from '@blocknote/react';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, View } from 'react-native';

import { DatabaseTable } from '@/components/databases/DatabaseTable.web';
import { stopBubbleUnlessFormField } from '@/lib/databases/stop-editor-bubble';
import { fetchBlock } from '@/lib/blocks/api';
import type { DatabaseViewConfig } from '@/lib/databases/view-config';
import {
  createMemoryViewStore,
  parseViewConfigJson,
  readViewConfigFromBlock,
  serializeViewConfig,
} from '@/lib/databases/view-config';

export const DATABASE_EMBED_BLOCK_TYPE = 'databaseEmbed' as const;

/** Legacy embed prop; independent viewConfigJson drives layout. */
export type DatabaseEmbedViewType = 'table' | 'board';

export const DEFAULT_DATABASE_EMBED_VIEW_TYPE: DatabaseEmbedViewType = 'table';

function stopEditorBubble(event: {
  stopPropagation: () => void;
  preventDefault?: () => void;
  target?: EventTarget | null;
}) {
  const target = event.target as HTMLElement | null;
  if (target?.closest?.('.cs-db-board-card')) {
    return;
  }
  event.stopPropagation();
}

type DatabaseEmbedViewProps = {
  databaseId: string;
  viewConfigJson: string;
  embedBlockId: string;
};

function DatabaseEmbedView({ databaseId, viewConfigJson, embedBlockId }: DatabaseEmbedViewProps) {
  const router = useRouter();
  const editor = useBlockNoteEditor();
  const [viewConfig, setViewConfig] = useState<DatabaseViewConfig>(() =>
    parseViewConfigJson(viewConfigJson),
  );

  useEffect(() => {
    if (viewConfigJson.trim()) {
      setViewConfig(parseViewConfigJson(viewConfigJson));
      return;
    }
    if (!databaseId) {
      return;
    }
    let cancelled = false;
    void fetchBlock(databaseId).then((block) => {
      if (!cancelled && block.type === 'database') {
        setViewConfig(readViewConfigFromBlock(block));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [databaseId, viewConfigJson]);

  const persistViewConfig = useCallback(
    async (next: DatabaseViewConfig) => {
      setViewConfig(next);
      const json = serializeViewConfig(next);
      const block = editor.getBlock(embedBlockId);
      if (block) {
        editor.updateBlock(block, {
          props: {
            ...block.props,
            viewConfigJson: json,
          },
        });
      }
    },
    [editor, embedBlockId],
  );

  const viewStore = useMemo(
    () =>
      createMemoryViewStore(
        () => viewConfig,
        persistViewConfig,
      ),
    [viewConfig, persistViewConfig],
  );

  if (!databaseId) {
    return (
      <View className="my-2 rounded-lg border border-border bg-surface-raised px-3 py-4">
        <Text className="text-sm text-fg-muted">Database is being created…</Text>
      </View>
    );
  }

  return (
    <div
      contentEditable={false}
      data-cs-database-embed={databaseId}
      className="my-2"
      onMouseDown={stopEditorBubble}
      onMouseUp={stopEditorBubble}
      onClick={stopEditorBubble}
      onPointerDown={stopEditorBubble}
      onKeyDown={stopEditorBubble}
      onKeyDownCapture={stopBubbleUnlessFormField}
    >
      <View className="cs-db-embed-shell">
        <DatabaseTable
          databaseId={databaseId}
          viewStore={viewStore}
          onOpenDatabase={() => router.push(`/database/${databaseId}`)}
          onOpenRow={(rowId) => router.push(`/page/${rowId}`)}
        />
      </View>
    </div>
  );
}

export const databaseEmbedBlock = createReactBlockSpec(
  {
    type: DATABASE_EMBED_BLOCK_TYPE,
    propSchema: {
      databaseId: { default: '' },
      viewType: { default: DEFAULT_DATABASE_EMBED_VIEW_TYPE },
      viewConfigJson: { default: '' },
    },
    content: 'none',
  },
  {
    render: (props) => (
      <DatabaseEmbedView
        databaseId={String(props.block.props.databaseId ?? '')}
        viewConfigJson={String(props.block.props.viewConfigJson ?? '')}
        embedBlockId={props.block.id}
      />
    ),
  },
);
